import * as i0 from '@angular/core';
import { Injectable, InjectionToken, isDevMode, APP_INITIALIZER, LOCALE_ID, NgModule, Inject, Pipe } from '@angular/core';
import { I18NEXT_SERVICE, I18NextModule } from 'angular-i18next';
import I18NextHttpBackend from 'i18next-http-backend';
import ICU from 'i18next-icu';

const DEFAULT_LABELS_NAMESPACE = 'smartphoto';
class AngularI18nService {
    convertDotNotationObject(inputObject = {}) {
        const result = {};
        if (!inputObject || Object.keys(inputObject).length === 0) {
            return result;
        }
        for (const key in inputObject) {
            const keys = key.split('.');
            let nestedObj = result;
            keys.forEach((nestedKey, index) => {
                if (!nestedObj[nestedKey]) {
                    if (index === keys.length - 1) {
                        nestedObj[nestedKey] = inputObject[key];
                    }
                    else {
                        nestedObj[nestedKey] = {};
                    }
                }
                nestedObj = nestedObj[nestedKey];
            });
        }
        return result;
    }
    async getLabelsFromExternalSource(url) {
        const response = await fetch(url);
        const externalLabels = await response.json();
        return externalLabels;
    }
    getLabelsUrl(app = '', url = '', customLabelsSheetName = '', language = '') {
        return `${url}/company_custom/${customLabelsSheetName}-${app}/${language}.json`;
    }
    async mergeLabelsFromExternalSource(labels, app, url, language, namespace = DEFAULT_LABELS_NAMESPACE, customLabelsSheetName) {
        let returnLabels = {
            ...labels
        };
        if (app && url && customLabelsSheetName) {
            const labelsUrl = this.getLabelsUrl(app, url, customLabelsSheetName, language);
            console.log('[angular-i18n] service labelsUrl', labelsUrl);
            try {
                // Load async the labels from S3
                const externalLabels = await this.getLabelsFromExternalSource(labelsUrl);
                // If there are labels in the response
                // Check that they are in the format { smartphoto: { ... } }
                // Transform labels to nested format
                const transformedExternalLabels = this.convertDotNotationObject(externalLabels);
                console.log('[angular-i18n] service transformedExternalLabels', transformedExternalLabels);
                // Merge transformed labels with customLabels
                returnLabels[namespace] = {
                    ...transformedExternalLabels
                };
            }
            catch (e) {
                console.error('[angular-i18n] mergeLabelsFromExternalSource', e.message || JSON.stringify(e));
            }
        }
        return returnLabels;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nService, deps: [], target: i0.ɵɵFactoryTarget.Injectable }); }
    static { this.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nService, providedIn: 'root' }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }] });

const Application = new InjectionToken('application');
const SUPPORTED_LANGUAGES = ['de', 'el', 'en', 'es', 'es-ES', 'es-MX', 'fi', 'fr', 'it', 'pt-BR', 'pt', 'sv', 'tr', 'th', 'th-TH'];
const appInit = function (i18next) {
    return () => {
        return i18next
            .use(ICU)
            .use(I18NextHttpBackend)
            .init({
            supportedLngs: SUPPORTED_LANGUAGES,
            lng: localStorage.getItem('language') || 'en',
            fallbackLng: 'en',
            debug: isDevMode(),
            returnEmptyString: false,
            ns: ['smartphoto', 'angular-container'],
            backend: {
                loadPath: '/assets/languages/{{ns}}/{{lng}}.json'
            }
        });
    };
};
const localeIdFactory = function (i18next) {
    return i18next.language;
};
const I18N_PROVIDERS = [
    {
        provide: APP_INITIALIZER,
        useFactory: appInit,
        deps: [I18NEXT_SERVICE],
        multi: true
    },
    {
        provide: LOCALE_ID,
        deps: [I18NEXT_SERVICE],
        useFactory: localeIdFactory
    }
];
class AngularI18nModule {
    static forRoot(config = {}) {
        const forRoot = I18NextModule.forRoot();
        forRoot.providers?.push(I18N_PROVIDERS);
        forRoot.providers?.push({
            provide: Application,
            useValue: config
        });
        return forRoot;
    }
    constructor(i18NextService, config, angularI18nService) {
        this.i18NextService = i18NextService;
        this.angularI18nService = angularI18nService;
        this.mergeCustomLabels(JSON.parse(window.localStorage.getItem('company_labels') || '{}'));
        const { application, publicUrl, labelsNamespace } = config;
        window.addEventListener('languageChanged', async (event) => this.loadLanguages(event, application, publicUrl, labelsNamespace), false);
    }
    async loadLanguages(event, app = '', url = '', namespace = '') {
        try {
            const { detail: { language = '', customLabels = {}, customLabelsSheetName = '' } = {} } = event || {};
            await this.i18NextService.loadLanguages([language]);
            const finalLabels = await this.angularI18nService.mergeLabelsFromExternalSource(customLabels, app, url, language, namespace, customLabelsSheetName);
            // This will merge the labels into the i18n service namespaces
            this.mergeCustomLabels(finalLabels);
            this.i18NextService.changeLanguage(language);
        }
        catch (e) {
            console.error('[angular-i18n] loadLanguages could not load', e.message || JSON.stringify(e));
        }
    }
    mergeCustomLabels(customLabels = {}) {
        for (let labelsNamespace in customLabels) {
            if (this.i18NextService.options.ns?.includes(labelsNamespace)) {
                for (let lang in this.i18NextService.store.data) {
                    this.i18NextService.addResourceBundle(lang, labelsNamespace, customLabels[labelsNamespace], true, true);
                }
            }
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nModule, deps: [{ token: I18NEXT_SERVICE }, { token: Application }, { token: AngularI18nService }], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nModule, imports: [I18NextModule], exports: [I18NextModule] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nModule, providers: [AngularI18nService], imports: [I18NextModule, I18NextModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [AngularI18nService],
                    imports: [I18NextModule],
                    exports: [I18NextModule]
                }]
        }], ctorParameters: () => [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [I18NEXT_SERVICE]
                }] }, { type: undefined, decorators: [{
                    type: Inject,
                    args: [Application]
                }] }, { type: AngularI18nService }] });

class I18NextEagerPipe {
    transform(value) {
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: I18NextEagerPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: I18NextEagerPipe, name: "i18nextEager" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: I18NextEagerPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'i18nextEager'
                }]
        }] });
class I18NextPipe {
    transform(value) {
        return value;
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: I18NextPipe, deps: [], target: i0.ɵɵFactoryTarget.Pipe }); }
    static { this.ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: I18NextPipe, name: "i18next" }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: I18NextPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'i18next'
                }]
        }] });
class AngularI18nTestingModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nTestingModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nTestingModule, declarations: [I18NextEagerPipe, I18NextPipe], exports: [I18NextEagerPipe, I18NextPipe] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nTestingModule, providers: [
            I18NextEagerPipe,
            I18NextPipe,
            {
                provide: I18NEXT_SERVICE,
                useValue: {
                    t: (key) => key.split(':').reverse()[0],
                    exists: (key) => true
                }
            },
            {
                provide: Application,
                useValue: {}
            }
        ] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AngularI18nTestingModule, decorators: [{
            type: NgModule,
            args: [{
                    providers: [
                        I18NextEagerPipe,
                        I18NextPipe,
                        {
                            provide: I18NEXT_SERVICE,
                            useValue: {
                                t: (key) => key.split(':').reverse()[0],
                                exists: (key) => true
                            }
                        },
                        {
                            provide: Application,
                            useValue: {}
                        }
                    ],
                    declarations: [I18NextEagerPipe, I18NextPipe],
                    exports: [I18NextEagerPipe, I18NextPipe]
                }]
        }] });

/*
 * Public API Surface of angular-i18n
 */

/**
 * Generated bundle index. Do not edit.
 */

export { AngularI18nModule, AngularI18nTestingModule, Application, I18N_PROVIDERS, I18NextEagerPipe, I18NextPipe, appInit, localeIdFactory };
